import { QueryClient, type QueryClient as QueryClientType } from '@tanstack/vue-query';
import { isAxiosError } from 'axios';

import objects from '~/utils/objects';

import { UseFinancingsParams } from './financing/useFinancings';
import { UseUsersParams } from './users/useUsers';

const queryClient = new QueryClient({
  defaultOptions: {
    mutations: {
      retry(_, error) {
        // if the error is a 401, don't retry
        if (isAxiosError(error) && error.response?.status === 401) {
          return false;
        }
        // otherwise, retry
        return true;
      },
      retryDelay(failureCount, error) {
        // if the error is a 401, don't retry
        if (isAxiosError(error) && error.response?.status === 401) {
          return 0;
        }
        // otherwise, retry after 1 second
        return failureCount * 1000;
      },
    },
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

// theses keys can be a function or a simple array
export const queryKeys = {
  AUTH: {
    LOGIN: ['AUTH', 'LOGIN'],
    LOGOUT: ['AUTH', 'LOGOUT'],
    STATUS: ['AUTH', 'STATUS'],
  },
  CONTRACT_FORMALIZATION: {
    INFO: (projectId: string) => ['CONTRACT_FORMALIZATION', 'INFO', projectId],
    RESEND_SIGNATURE: (projectId: string) => ['CONTRACT_FORMALIZATION', 'RESEND_SIGNATURE', projectId],
  },
  FINANCING: {
    DETAILS: (id: string) => ['FINANCING', id],
    LIST: (params: UseFinancingsParams) => ['FINANCING', 'LIST', ...objects.objectParamsToString(params)],
  },
  FORMALIZATION: {
    DOWNLOAD_DDC: (financingId: string) => ['FORMALIZATION', 'DOWNLOAD_DDC', financingId],
  },
  PARTNER: {
    DETAILS: (id: number | string) => ['PARTNER', id],
  },
  PARTNER_USERS: {
    DETAILS: (id: null | number) => ['PARTNER_USERS', 'DETAILS', id],
    LIST: (partnerId: number, page: number, pageSize: number) => [
      'PARTNER_USERS',
      'LIST',
      partnerId,
      page,
      pageSize,
    ],
    LIST_ALL: ['PARTNER_USERS', 'LIST'],
  },
  PROJECT: {
    CANCEL: (financingId: number) => ['PROJECT', 'CANCEL', financingId],
  },
  REGISTER_PARTNER: {
    DETAILS: (partnerId: number) => ['REGISTER_PARTNER', 'DETAILS', partnerId],
    DETAILS_ALL: ['REGISTER_PARTNER', 'DETAILS'],
  },
  USERS: {
    DETAILS: (id: string) => ['USERS', id],
    LIST: (params: UseUsersParams) => ['USERS', 'LIST', ...objects.objectParamsToString(params)],
  },
};

export async function invalidatePartnerDetailsQueries(
  client: QueryClientType,
  partnerId?: number,
) {
  const invalidations = [
    client.invalidateQueries({
      queryKey: queryKeys.REGISTER_PARTNER.DETAILS_ALL,
      refetchType: 'active',
    }),
  ];

  if (partnerId && partnerId > 0) {
    invalidations.push(
      client.invalidateQueries({
        queryKey: queryKeys.REGISTER_PARTNER.DETAILS(partnerId),
        refetchType: 'active',
      }),
    );
  }

  await Promise.all(invalidations);
}

export async function invalidatePartnerUsersQueries(
  client: QueryClientType,
  partnerId?: number,
) {
  const invalidations = [
    client.invalidateQueries({
      queryKey: queryKeys.PARTNER_USERS.LIST_ALL,
      refetchType: 'active',
    }),
  ];

  if (partnerId && partnerId > 0) {
    invalidations.push(
      client.invalidateQueries({
        queryKey: ['PARTNER_USERS', 'LIST', partnerId],
        refetchType: 'active',
      }),
    );
  }

  await Promise.all(invalidations);
}

export default queryClient;
